<template>
    <div class="new-translate-panel" :class="{ 'light-theme': true }">
        <div class="translate-header">
            <div class="translate-title" v-if="!notupdate">
                <span class="title-text">Translate into</span>
                <el-select :disabled="isButtonDisabled" v-model="selectedLanguage" placeholder="Select Language"
                    size="small" class="language-select">
                    <el-option v-for="lang in languages" :key="lang.code" :label="lang.fullname" :value="lang.name">
                        <span class="language-flag" style="margin-right: 12px;">{{ lang.emoji }}</span>
                        <span class="language-name">{{ lang.fullname }}</span>
                    </el-option>
                </el-select>
                <el-button type="primary" size="small" @click="startTranslate" :loading="isTranslating"
                    :disabled="isButtonDisabled" class="translate-btn">
                    {{ getButtonText }}
                </el-button>
            </div>
            <div class="translate-actions" v-if="translatedContent">
                <el-tooltip content="Copy as Markdown" placement="top">
                    <el-button type="text" icon="el-icon-document" @click="copyMarkdown"></el-button>
                </el-tooltip>
                <el-tooltip content="Copy as Text" placement="top">
                    <el-button type="text" icon="el-icon-tickets" @click="copyText"></el-button>
                </el-tooltip>
                <el-tooltip content="Download as PDF" placement="top">
                    <el-button type="text" icon="el-icon-download" @click="downloadAsPdf"></el-button>
                </el-tooltip>
            </div>
        </div>

        <div class="translate-content">
            <div class="guide-tips" v-if="!translatedContent && !isTranslating">
                <div class="tip-box tip-1">
                    <div class="tip-icon">
                        <i class="el-icon-info"></i>
                    </div>
                    <div class="tip-content">
                        <h3>Step 1</h3>
                        <p>Select your target language and click "Get it Now" to start translation</p>
                    </div>
                </div>
                <div class="tip-box tip-2">
                    <div class="tip-icon">
                        <i class="el-icon-document"></i>
                    </div>
                    <div class="tip-content">
                        <h3>Step 2</h3>
                        <p>After translation, you can copy the content as Markdown or Text format</p>
                    </div>
                </div>
            </div>
            <div v-else-if="!translatedContent && isTranslating" class="loading-container">
                <div class="loading-animation">
                    <el-progress type="circle" :percentage="translateProgress" :stroke-width="6" color="#3498db"></el-progress>
                </div>
                <p class="loading-text">Translating your notes...</p>
                <p class="loading-subtext">This may take a moment depending on the content length</p>
            </div>
            <div class="translated-text" ref="contentToDownload" v-else v-html="translatedContent"></div>
        </div>
    </div>
</template>

<script>
import MarkdownIt from "markdown-it";
import hljs from "highlight.js";

// 常量定义
const CONSTANTS = {
    PROGRESS_UPDATE_INTERVAL: 1000,     // 进度更新间隔
    TRANSLATE_TIMEOUT: 120000,         // 翻译超时时间
    POLL_INTERVAL: 3000,              // 轮询间隔
    DISABLE_DURATION: 60000,          // 禁用时间
    CACHE_EXPIRY: 24 * 60 * 60 * 1000, // 24小时
    MAX_POLL_ATTEMPTS: 400,            // 最大轮询次数
    ANIMATION_DELAYS: {
        HEADER: 100,
        TIP_BASE: 200,
        TIP_INTERVAL: 150,
        CONTENT: 100
    }
};

// 工具函数
const utils = {
    // 防抖函数
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },

    // 缓存管理
    cache: {
        set(key, value, expiry = CONSTANTS.CACHE_EXPIRY) {
            try {
                const item = {
                    value,
                    timestamp: Date.now(),
                    expiry
                };
                localStorage.setItem(key, JSON.stringify(item));
                return true;
            } catch (e) {
                console.error('缓存保存失败:', e);
                return false;
            }
        },

        get(key) {
            try {
                const item = JSON.parse(localStorage.getItem(key));
                if (!item) return null;

                const now = Date.now();
                if (now - item.timestamp > item.expiry) {
                    localStorage.removeItem(key);
                    return null;
                }
                return item.value;
            } catch (e) {
                console.error('缓存读取失败:', e);
                return null;
            }
        },

        remove(key) {
            try {
                localStorage.removeItem(key);
                return true;
            } catch (e) {
                console.error('缓存删除失败:', e);
                return false;
            }
        }
    },

    // 复制到剪贴板
    async copyToClipboard(text) {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            } else {
                // 降级方案
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                const result = document.execCommand('copy');
                document.body.removeChild(textArea);
                return result;
            }
        } catch (error) {
            console.error('复制失败:', error);
            return false;
        }
    },

    // HTML转文本
    htmlToText(html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || '';
    },

    // 获取翻译缓存key
    getTranslateCacheKey(noteId, language) {
        return `translate_${noteId}_${language}`;
    }
};

export default {
    name: 'TranslateToggle',
    
    data() {
        return {
            selectedLanguage: 'English',
            languages: [
                { code: "EN", name: "English", fullname: "English", emoji: "🇬🇧" },
                { code: "ZH", name: "Chinese", fullname: "简体中文", emoji: "🇨🇳" },
                { code: "ZH(T)", name: "Traditional Chinese (Hong Kong)", fullname: "繁体中文", emoji: "🇭🇰" },
                { code: "ES", name: "Spanish", fullname: "Español", emoji: "🇪🇸" },
                { code: "FR", name: "French", fullname: "Français", emoji: "🇫🇷" },
                { code: "DE", name: "German", fullname: "Deutsch", emoji: "🇩🇪" },
                { code: "IT", name: "Italian", fullname: "Italiano", emoji: "🇮🇹" },
                { code: "JA", name: "Japanese", fullname: "日本語", emoji: "🇯🇵" },
                { code: "RU", name: "Russian", fullname: "Русский", emoji: "🇷🇺" },
                { code: "AR", name: "Arabic", fullname: "العربية", emoji: "🇸🇦" },
                { code: "HI", name: "Hindi", fullname: "हिन्दी", emoji: "🇮🇳" },
                { code: "PT", name: "Portuguese", fullname: "Português", emoji: "🇵🇹" },
                { code: "NL", name: "Dutch", fullname: "Nederlands", emoji: "🇳🇱" },
                { code: "KO", name: "Korean", fullname: "한국어", emoji: "🇰🇷" },
                { code: "TR", name: "Turkish", fullname: "Türkçe", emoji: "🇹🇷" },
            ],

            md: new MarkdownIt({
                html: true,
                linkify: true,
                typographer: true,
                highlight: function (str, lang) {
                    if (lang && hljs.getLanguage(lang)) {
                        try {
                            return hljs.highlight(lang, str).value;
                        } catch (__) {
                            console.log("highlight error", str, lang);
                        }
                    }
                    return "";
                },
            }),

            // 状态管理
            state: {
                noteTranslate: '',
                isTranslating: false,
                hasTranslated: false,
                translateProgress: 0,
                disableEndTime: null,
                currentTimestamp: Date.now(),
                isPolling: false,
                pollAttempts: 0
            },

            // 定时器管理
            timers: {
                progress: null,
                disable: null,
                poll: null
            }
        }
    },

    props: {
        noteobj: {
            type: Object,
            default: null
        },
        notupdate: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        translatedContent() {
            return this.state.noteTranslate ? this.md.render(this.state.noteTranslate) : '';
        },

        isTranslating() {
            return this.state.isTranslating;
        },

        isButtonDisabled() {
            return this.state.isTranslating || 
                   (this.state.disableEndTime && this.state.currentTimestamp < this.state.disableEndTime);
        },

        getButtonText() {
            if (this.state.isTranslating) {
                return 'Translating...';
            }
            if (this.state.disableEndTime && this.state.currentTimestamp < this.state.disableEndTime) {
                const remainingSeconds = Math.ceil((this.state.disableEndTime - this.state.currentTimestamp) / 1000);
                return `wait ${remainingSeconds}s`;
            }
            return 'Get it Now';
        },

        translateProgress() {
            return this.state.translateProgress;
        }
    },

    watch: {
        noteobj: {
            handler: 'handleNoteObjChange',
            deep: true
        },
        'noteobj.noteid': {
            handler: 'handleNoteIdChange',
            immediate: true
        },
        selectedLanguage: 'handleLanguageChange'
    },

    mounted() {
        this.initializeComponent();
    },

    beforeDestroy() {
        this.cleanup();
    },

    methods: {
        // 初始化组件
        initializeComponent() {
            this.initializeTranslation();
            this.addEntranceAnimation();
            console.log("当前路由:", this.$route.path);
        },

        // 初始化翻译状态
        initializeTranslation() {
            if (this.noteobj?.note_translate) {
                this.state.noteTranslate = this.noteobj.note_translate;
                this.state.hasTranslated = true;
            } else {
                this.restoreFromCache();
                if (!this.state.noteTranslate) {
                    this.checkTranslatingState();
                }
            }
        },

        // 处理noteobj变化
        handleNoteObjChange(newVal) {
            console.log("noteobj changed:", newVal);
            
            if (newVal?.note_translate) {
                this.state.noteTranslate = newVal.note_translate;
                this.state.hasTranslated = true;
            } else {
                this.restoreFromCache();
                if (!this.state.noteTranslate) {
                    this.checkTranslatingState();
                }
            }
        },

        // 处理笔记ID变化
        handleNoteIdChange(newVal) {
            if (newVal) {
                this.resetTranslationState();
                this.$nextTick(() => {
                    this.restoreFromCache();
                });
            }
        },

        // 处理语言变化
        handleLanguageChange(newVal, oldVal) {
            if (newVal !== oldVal && oldVal) {
                this.state.hasTranslated = false;
                this.state.noteTranslate = '';
                
                this.$nextTick(() => {
                    this.restoreFromCache();
                });
            }
        },

        // 重置翻译状态
        resetTranslationState() {
            this.state.hasTranslated = false;
            this.state.isTranslating = false;
            this.state.noteTranslate = '';
            this.clearProgressAnimation();
        },

        // 开始翻译
        async startTranslate() {
            // 验证输入
            if (!this.validateTranslateInput()) {
                return;
            }

            // 重置状态
            this.resetTranslationForNewRequest();
            
            // 开始翻译流程
            this.state.isTranslating = true;
            this.startProgressAnimation();

            try {
                const noteText = this.extractNoteText();
                const result = await this.requestTranslation(noteText);
                
                if (result.success) {
                    this.setTranslatingState();
                    this.updateNoteObj('');
                    await this.pollTranslationResult(result.noteid);
                } else {
                    throw new Error(result.message);
                }
            } catch (error) {
                this.handleTranslationError(error);
            }
        },

        // 验证翻译输入
        validateTranslateInput() {
            if (!this.selectedLanguage) {
                this.$message.warning('Please select a language first');
                return false;
            }

            if (this.selectedLanguage === this.noteobj?.language) {
                this.$message.warning('Please change to a different language');
                return false;
            }

            if (this.state.isTranslating || this.state.hasTranslated) {
                return false;
            }

            return true;
        },

        // 重置翻译状态为新请求
        resetTranslationForNewRequest() {
            if (this.state.noteTranslate) {
                this.state.noteTranslate = '';
                this.state.hasTranslated = false;
                this.state.isTranslating = false;
                this.clearProgressAnimation();
            }
        },

        // 提取笔记文本
        extractNoteText() {
            if (this.noteobj?.filetype === 'record' && this.noteobj?.live_transcript) {
                return this.extractRecordNoteText();
            }
            return this.noteobj?.note_text || '';
        },

        // 提取录音笔记文本
        extractRecordNoteText() {
            try {
                const liveTranscript = typeof this.noteobj.live_transcript === 'string' 
                    ? JSON.parse(this.noteobj.live_transcript) 
                    : this.noteobj.live_transcript;
                
                if (liveTranscript?.part_objs?.length > 0) {
                    return liveTranscript.part_objs
                        .map(item => item.notes || '')
                        .join('');
                }
            } catch (e) {
                console.error('解析live_transcript错误:', e);
            }
            return this.noteobj?.note_text || '';
        },

        // 请求翻译
        async requestTranslation(noteText) {
            const subid = this.$cookies.get('subid');
            
            try {
                const res = await this.$axios.post('/api/set_translate_note/', {
                    noteid: this.noteobj.noteid,
                    language: this.selectedLanguage,
                    note_text: noteText,
                    subid: subid,
                });

                const redata = res.data;
                if (redata.code === 200) {
                    return {
                        success: true,
                        noteid: redata.noteid
                    };
                } else {
                    return {
                        success: false,
                        message: redata.messages
                    };
                }
            } catch (error) {
                return {
                    success: false,
                    message: "Failed to start translation"
                };
            }
        },

        // 设置翻译中状态
        setTranslatingState() {
            this.$cookies.set('translating_note', JSON.stringify({
                noteid: this.noteobj.noteid,
                language: this.selectedLanguage,
                timestamp: Date.now()
            }), '1d');
        },

        // 轮询翻译结果
        async pollTranslationResult(noteid) {
            if (this.state.isPolling) {
                return;
            }

            this.state.isPolling = true;
            this.state.pollAttempts = 0;

            const poll = async () => {
                try {
                    this.state.pollAttempts++;
                    
                    if (this.state.pollAttempts > CONSTANTS.MAX_POLL_ATTEMPTS) {
                        throw new Error('Translation timeout - please try again');
                    }

                    const res = await this.$axios.post('/api/get_translate_note/', {
                        noteid: noteid,
                    });

                    const redata = res.data;
                    
                    if (redata.code === 200) {
                        this.handleTranslationSuccess(redata.note_translate);
                    } else if (redata.code === 202) {
                        this.scheduleNextPoll(poll);
                    } else {
                        throw new Error(redata.messages || 'Translation failed');
                    }
                } catch (error) {
                    this.handleTranslationError(error);
                }
            };

            await poll();
        },

        // 处理翻译成功
        handleTranslationSuccess(translatedText) {
            this.state.noteTranslate = translatedText;
            this.state.isTranslating = false;
            this.state.hasTranslated = true;
            this.clearProgressAnimation();
            this.state.isPolling = false;
            
            this.$message.success("Translation completed successfully");
            this.startDisableTimer();
            this.clearTranslatingState();
            this.updateNoteObj(translatedText);
            this.saveToCache(translatedText);
            
            this.$nextTick(() => {
                this.animateContentAppear();
            });

            this.clearPollTimer();
        },

        // 安排下次轮询
        scheduleNextPoll(pollFunction) {
            this.timers.poll = setTimeout(() => pollFunction(), CONSTANTS.POLL_INTERVAL);
        },

        // 处理翻译错误
        handleTranslationError(error) {
            this.state.isTranslating = false;
            this.clearProgressAnimation();
            this.state.isPolling = false;
            this.$message.error(error.message || "Translation failed");
            this.clearPollTimer();
        },

        // 更新noteobj
        updateNoteObj(translatedText) {
            const updatedNoteobj = { ...this.noteobj };
            updatedNoteobj.note_translate = translatedText;
            this.$emit('setnoteobj', updatedNoteobj);
        },

        // 保存到缓存
        saveToCache(translatedText) {
            if (this.noteobj?.noteid && this.selectedLanguage) {
                const cacheKey = utils.getTranslateCacheKey(this.noteobj.noteid, this.selectedLanguage);
                utils.cache.set(cacheKey, translatedText);
            }
        },

        // 从缓存恢复
        restoreFromCache() {
            if (!this.noteobj?.noteid || !this.selectedLanguage) {
                return false;
            }
            
            const cacheKey = utils.getTranslateCacheKey(this.noteobj.noteid, this.selectedLanguage);
            const cachedTranslation = utils.cache.get(cacheKey);
            
            if (cachedTranslation) {
                this.state.noteTranslate = cachedTranslation;
                this.state.hasTranslated = true;
                this.updateNoteObj(cachedTranslation);
                return true;
            }
            
            return false;
        },

        // 检查翻译状态
        async checkTranslatingState() {
            const translatingNote = JSON.parse(this.$cookies.get('translating_note') || '{}');
            
            // 首先尝试从缓存恢复
            if (this.restoreFromCache()) {
                return;
            }
            
            // 检查是否有正在进行的翻译任务
            if (translatingNote?.noteid === this.noteobj?.noteid && !this.state.noteTranslate) {
                if (!this.noteobj.noteid) return;
                
                this.state.isTranslating = true;
                this.selectedLanguage = translatingNote.language;
                this.startProgressAnimation();
                await this.pollTranslationResult(this.noteobj.noteid);
            }
        },

        // 进度动画
        startProgressAnimation() {
            this.state.translateProgress = 0;
            const startTime = Date.now();

            this.timers.progress = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                const progress = Math.min(99, Math.floor((elapsedTime / CONSTANTS.TRANSLATE_TIMEOUT) * 100));

                if (progress >= 99) {
                    clearInterval(this.timers.progress);
                    this.timers.progress = null;
                }

                this.state.translateProgress = progress;
            }, CONSTANTS.PROGRESS_UPDATE_INTERVAL);
        },

        clearProgressAnimation() {
            if (this.timers.progress) {
                clearInterval(this.timers.progress);
                this.timers.progress = null;
            }
            this.state.translateProgress = 0;
        },

        // 禁用定时器
        startDisableTimer() {
            this.state.disableEndTime = Date.now() + CONSTANTS.DISABLE_DURATION;

            if (this.timers.disable) {
                clearInterval(this.timers.disable);
            }

            this.timers.disable = setInterval(() => {
                this.state.currentTimestamp = Date.now();
                if (this.state.currentTimestamp >= this.state.disableEndTime) {
                    this.clearDisableState();
                }
            }, 1000);
        },

        clearDisableState() {
            if (this.timers.disable) {
                clearInterval(this.timers.disable);
                this.timers.disable = null;
            }
            this.state.disableEndTime = null;
        },

        // 清理方法
        clearTranslatingState() {
            this.$cookies.remove('translating_note');
        },

        clearPollTimer() {
            if (this.timers.poll) {
                clearTimeout(this.timers.poll);
                this.timers.poll = null;
            }
        },

        cleanup() {
            // 清理所有定时器
            Object.values(this.timers).forEach(timer => {
                if (timer) {
                    clearTimeout(timer);
                    clearInterval(timer);
                }
            });
            
            // 重置定时器对象
            this.timers = {
                progress: null,
                disable: null,
                poll: null
            };
            
            this.state.isPolling = false;
            
            // 保存当前翻译结果到缓存
            if (this.noteobj?.noteid && this.selectedLanguage && this.state.noteTranslate) {
                this.saveToCache(this.state.noteTranslate);
            }
        },

        // 复制方法
        async copyMarkdown() {
            const success = await utils.copyToClipboard(this.state.noteTranslate);
            if (success) {
                this.$message({ 
                    message: "Copied to clipboard", 
                    type: "success",
                    customClass: 'copy-success-message'
                });
            } else {
                this.$message.error("Failed to copy");
            }
        },

        async copyText() {
            const textContent = utils.htmlToText(this.translatedContent);
            const success = await utils.copyToClipboard(textContent);
            if (success) {
                this.$message({ 
                    message: "Copied to clipboard", 
                    type: "success",
                    customClass: 'copy-success-message'
                });
            } else {
                this.$message.error("Failed to copy");
            }
        },

        // PDF下载
        downloadAsPdf() {
            this.printPdf('contentToDownload');
        },

        async printPdf(resobj) {
            try {
                const printContainer = this.$refs[resobj];
                if (!printContainer || !printContainer.innerHTML.trim()) {
                    throw new Error('No content to print');
                }

                const iframe = document.createElement('iframe');
                iframe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;';
                document.body.appendChild(iframe);

                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                iframeDoc.open();
                iframeDoc.write(`
                    <html>
                        <head>
                            <title>打印内容</title>
                            <style>
                                body { font-family: Arial, sans-serif; }
                                .print-container { padding: 20px; }
                                ${this.getAdditionalStyles()}
                            </style>
                        </head>
                        <body>${printContainer.innerHTML}</body>
                    </html>
                `);
                iframeDoc.close();

                await new Promise(resolve => {
                    iframe.onload = resolve;
                });

                await new Promise(resolve => setTimeout(resolve, 1000));

                iframe.contentWindow.print();
                document.body.removeChild(iframe);
            } catch (error) {
                console.error('PDF打印失败:', error);
                this.$message.error('PDF打印失败，请稍后重试');
            }
        },

        getAdditionalStyles() {
            return `
                .custom-class { color: #333; }
            `;
        },

        // 动画方法
        addEntranceAnimation() {
            setTimeout(() => {
                this.animateHeader();
                this.animateTips();
            }, 300);
        },

        animateHeader() {
            const header = document.querySelector('.translate-header');
            if (header) {
                header.style.cssText = 'opacity:0;transform:translateY(-20px);transition:all 0.5s ease;';
                setTimeout(() => {
                    header.style.cssText = 'opacity:1;transform:translateY(0);transition:all 0.5s ease;';
                }, CONSTANTS.ANIMATION_DELAYS.HEADER);
            }
        },

        animateTips() {
            const tips = document.querySelectorAll('.tip-box');
            tips.forEach((tip, index) => {
                tip.style.cssText = 'opacity:0;transform:translateX(-20px);transition:all 0.5s ease;';
                setTimeout(() => {
                    tip.style.cssText = 'opacity:1;transform:translateX(0);transition:all 0.5s ease;';
                }, CONSTANTS.ANIMATION_DELAYS.TIP_BASE + index * CONSTANTS.ANIMATION_DELAYS.TIP_INTERVAL);
            });
        },

        animateContentAppear() {
            const content = this.$refs.contentToDownload;
            if (content) {
                content.style.cssText = 'opacity:0;transform:translateY(20px);transition:all 0.8s ease;';
                setTimeout(() => {
                    content.style.cssText = 'opacity:1;transform:translateY(0);transition:all 0.8s ease;';
                }, CONSTANTS.ANIMATION_DELAYS.CONTENT);
            }
        }
    }
}
</script>

<style lang="less" scoped>
.new-translate-panel {
    flex: 1;
    max-height: calc(100vh - 130px);
    overflow: auto;
    color: #2c3e50;
    padding: 24px;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
    margin: 8px;
    height: calc(100% - 20px);
    position: relative;
    
    /* 自定义滚动条样式 */
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.02);
        border-radius: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.08);
        border-radius: 10px;
        transition: all 0.3s ease;
        
        &:hover {
            background: rgba(0, 0, 0, 0.15);
        }
    }
    
    .translate-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        padding-bottom: 18px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        position: relative;

        .translate-title {
            display: flex;
            align-items: center;
            gap: 12px;
            white-space: nowrap;
            min-width: 0;

            .title-text {
                font-size: 16px;
                font-weight: 500;
                letter-spacing: 0.5px;
                text-transform: capitalize;
                color: #2c3e50;
                margin-right: 4px;
                white-space: nowrap;
            }

            :deep(.el-select) {
                width: 150px;
                margin-right: 12px;

                .el-input__inner {
                    border-radius: 8px;
                    padding-left: 12px;
                    transition: all 0.3s ease;
                    border: 1px solid #e8e8e8;
                    height: 36px;
                    
                    &:hover, &:focus {
                        border-color: #3498db;
                    }
                }
            }

            .translate-btn {
                border-radius: 8px;
                padding: 0 16px;
                font-weight: 500;
                transition: all 0.3s ease;
                background: #3498db;
                border-color: #3498db;
                height: 36px;
                white-space: nowrap;
                min-width: 100px;
                flex-shrink: 0;
                
                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.25);
                    background: #2980b9;
                    border-color: #2980b9;
                }
            }
        }

        .translate-actions {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-left: 20px;
            flex-shrink: 0;
            
            :deep(.el-button--text) {
                color: #5e6d82;
                font-size: 18px;
                border-radius: 8px;
                background: rgba(0, 0, 0, 0.03);
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;
                padding: 0;
                margin: 0;
                
                &:hover {
                    background: rgba(52, 152, 219, 0.1);
                    color: #3498db;
                    transform: translateY(-2px);
                }
            }
        }
    }

    .translate-content {
        flex: 1;
        overflow-y: auto;
        border-radius: 12px;
        position: relative;
        margin-bottom: 8px;
        
        /* 自定义滚动条样式 */
        &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }
        
        &::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.02);
            border-radius: 8px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.08);
            border-radius: 8px;
            transition: all 0.3s ease;
            
            &:hover {
                background: rgba(0, 0, 0, 0.15);
            }
        }

        .guide-tips {
            display: flex;
            flex-direction: column;
            gap: 24px;
            padding: 24px 10%;

            .tip-box {
                display: flex;
                align-items: center;
                background: #f8f9fa;
                border-radius: 12px;
                padding: 24px;
                gap: 20px;
                border: 1px solid rgba(0, 0, 0, 0.04);
                transition: all 0.3s ease;
                
                &:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
                }

                .tip-icon {
                    width: 48px;
                    height: 48px;
                    background: rgba(52, 152, 219, 0.1);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    color: #3498db;
                    font-size: 24px;
                }

                .tip-content {
                    h3 {
                        margin: 0 0 8px 0;
                        font-size: 18px;
                        font-weight: 600;
                        color: #2c3e50;
                    }

                    p {
                        margin: 0;
                        color: #5e6d82;
                        line-height: 1.6;
                    }
                }
            }
        }

        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 48px;
            height: 100%;
            min-height: 300px;
            
            .loading-animation {
                margin-bottom: 24px;
            }
            
            .loading-text {
                font-size: 18px;
                font-weight: 600;
                color: #2c3e50;
                margin: 16px 0 8px;
            }
            
            .loading-subtext {
                font-size: 14px;
                color: #7f8c8d;
                margin: 0;
            }
        }

        .translated-text {
            padding: 24px;
            line-height: 1.8;
            color: #2c3e50;
            background: #f9fafb;
            border-radius: 12px;
            min-height: 300px;
            
            :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
                color: #2c3e50;
                margin-top: 1.5em;
                margin-bottom: 0.8em;
                font-weight: 600;
                line-height: 1.3;
            }
            
            :deep(h1) {
                font-size: 2em;
                border-bottom: 1px solid #eaecef;
                padding-bottom: 0.3em;
            }
            
            :deep(h2) {
                font-size: 1.5em;
                border-bottom: 1px solid #eaecef;
                padding-bottom: 0.3em;
            }
            
            :deep(h3) {
                font-size: 1.25em;
            }
            
            :deep(p) {
                margin: 1em 0;
            }
            
            :deep(a) {
                color: #3498db;
                text-decoration: none;
                
                &:hover {
                    text-decoration: underline;
                }
            }
            
            :deep(strong) {
                font-weight: 600;
            }
            
            :deep(ul), :deep(ol) {
                padding-left: 1.5em;
                margin: 1em 0;
            }
            
            :deep(li) {
                margin: 0.5em 0;
            }
            
            :deep(code) {
                background-color: rgba(0, 0, 0, 0.05);
                padding: 0.2em 0.4em;
                border-radius: 3px;
                font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                font-size: 0.9em;
            }
            
            :deep(pre) {
                background-color: #282c34;
                border-radius: 8px;
                padding: 16px;
                overflow-x: auto;
                margin: 1.5em 0;
                
                code {
                    background-color: transparent;
                    padding: 0;
                    font-size: 0.9em;
                    color: #e6e6e6;
                }
            }
            
            :deep(blockquote) {
                border-left: 4px solid #3498db;
                margin: 1.5em 0;
                padding: 0.5em 1em;
                color: #5e6d82;
                background-color: rgba(52, 152, 219, 0.05);
                border-radius: 0 8px 8px 0;
            }
            
            :deep(img) {
                max-width: 100%;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            :deep(table) {
                border-collapse: collapse;
                width: 100%;
                margin: 1.5em 0;
                overflow-x: auto;
                display: block;
                
                th, td {
                    border: 1px solid #eaecef;
                    padding: 8px 12px;
                }
                
                th {
                    background-color: #f5f5f5;
                    font-weight: 600;
                }
                
                tr:nth-child(even) {
                    background-color: #f9f9f9;
                }
            }
        }
    }
}

.tip-selector {
    margin-bottom: 24px;
    display: flex;
    gap: 12px;
    
    .tip-option {
        flex: 1;
        font-size: 14px;
        padding: 10px 20px;
        
        &:hover {
            background-color: rgba(52, 152, 219, 0.1);
            color: #3498db;
        }
        
        i {
            margin-right: 10px;
            font-size: 16px;
        }
    }
}

:deep(.el-button--text) {
    color: #5e6d82;

    &:hover {
        color: #3498db;
    }
}

:deep(.el-select .el-input__inner) {
    background-color: #ffffff;
    border: 1px solid #e8e8e8;
    color: #2c3e50;
    
    &:focus {
        border-color: #3498db;
    }
}

:deep(.el-select-dropdown) {
    background-color: #ffffff;
    border: none;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    
    .el-select-dropdown__item {
        color: #2c3e50;
        
        &.selected {
            color: #3498db;
            font-weight: 600;
        }
        
        &:hover {
            background-color: rgba(52, 152, 219, 0.1);
        }
        
        .language-flag {
            font-size: 16px;
            margin-right: 8px;
        }
        
        .language-name {
            font-size: 14px;
        }
    }
}

:deep(.el-progress) {
    .el-progress__text {
        color: #2c3e50;
        font-weight: 600;
        font-size: 18px;
    }
}

:deep(.el-button.is-disabled) {
    background-color: #e8e8e8;
    border-color: #e8e8e8;
    color: #ffffff;

    &:hover {
        background-color: #e8e8e8;
        border-color: #e8e8e8;
    }
}

:deep(.el-tooltip__popper) {
    background: #ffffff;
    border: 1px solid #e8e8e8;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    font-size: 12px;
    padding: 8px 12px;
    color: #2c3e50;
    
    &.is-dark {
        background: #2c3e50;
        color: #ffffff;
    }
    
    .popper__arrow {
        border-bottom-color: #ffffff !important;
        
        &:after {
            border-bottom-color: #ffffff !important;
        }
    }
}

// 添加消息提示样式
:deep(.copy-success-message) {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-left: 4px solid #3498db;
    
    .el-message__content {
        color: #2c3e50;
        font-weight: 500;
    }
    
    .el-message__icon {
        color: #3498db;
    }
}

// 添加响应式样式
@media (max-width: 900px) {
    .new-translate-panel {
        .translate-header {
            flex-wrap: nowrap;
            
            .translate-title {
                flex: 1;
                min-width: 0;
                
                :deep(.el-select) {
                    width: 120px;
                    max-width: 35%;
                }
                
                .translate-btn {
                    min-width: 90px;
                }
            }
            
            .translate-actions {
                margin-left: 12px;
                gap: 12px;
            }
        }
    }
}

@media (max-width: 768px) {
    .new-translate-panel {
        padding: 20px;
        
        .translate-header {
            flex-direction: row;
            flex-wrap: wrap;
            margin-bottom: 20px;
            
            .translate-title {
                flex: 1;
                flex-wrap: nowrap;
                overflow: hidden;
                margin-bottom: 4px;
                
                :deep(.el-select) {
                    width: 120px;
                    max-width: 35%;
                    flex-shrink: 1;
                }
                
                .translate-btn {
                    min-width: 90px;
                    flex-shrink: 0;
                }
            }
            
            .translate-actions {
                gap: 10px;
                margin-left: 8px;
                
                :deep(.el-button--text) {
                    width: 36px;
                    height: 36px;
                    font-size: 16px;
                }
            }
        }
        
        .translate-content {
            .guide-tips {
                padding: 20px 5%;
                gap: 20px;
                
                .tip-box {
                    padding: 20px;
                    gap: 16px;
                    
                    .tip-icon {
                        width: 40px;
                        height: 40px;
                        font-size: 20px;
                    }
                    
                    .tip-content h3 {
                        font-size: 16px;
                    }
                }
            }
            
            .loading-container {
                padding: 30px;
            }

            .translated-text {
                padding: 20px 16px;
            }
        }
    }
}

@media (max-width: 500px) {
    .translate-panel {
        .translate-header {
            flex-direction: column;
            align-items: flex-start;
            
            .translate-title {
                width: 100%;
                margin-bottom: 16px;
                
                .title-text {
                    font-size: 15px;
                }
                
                :deep(.el-select) {
                    width: 50%;
                    max-width: none;
                    margin-right: 8px;
                }
                
                .translate-btn {
                    min-width: 80px;
                    font-size: 14px;
                }
            }
            
            .translate-actions {
                align-self: flex-end;
                margin-left: 0;
                gap: 8px;
                
                :deep(.el-button--text) {
                    width: 32px;
                    height: 32px;
                }
            }
        }
        
        .translate-content {
            .guide-tips {
                padding: 16px 0;
                
                .tip-box {
                    padding: 16px;
                    
                    .tip-content p {
                        font-size: 14px;
                    }
                }
            }
        }
    }
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        opacity: 0.7;
    }
    50% {
        transform: scale(1);
        opacity: 1;
    }
    100% {
        transform: scale(0.95);
        opacity: 0.7;
    }
}
</style>
